import { useState, useRef, useEffect } from 'react';
import { cn } from '../lib/cn.js';

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function parseIso(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'jj/mm/aaaa',
  className,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Re-read locale on every render so it follows i18next changes to <html lang>.
  const locale = typeof document !== 'undefined' ? document.documentElement.lang || 'en' : 'en';

  const selectedDate = parseIso(value);
  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? new Date().getMonth());

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build month names using Intl for the active locale
  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Date(2024, i, 1).toLocaleDateString(locale, { month: 'long' }),
  );
  // Build day names (Monday-first, shorter form)
  // We start from Monday (day 1) to Sunday (day 0)
  const dayNames = Array.from({ length: 7 }, (_, i) => {
    const dayIndex = i + 1; // 1=Mon ... 6=Sat, 7=Sun → 0
    const d = new Date(2024, 0, dayIndex);
    return d.toLocaleDateString(locale, { weekday: 'short' });
  });

  const totalDays = daysInMonth(viewYear, viewMonth);
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const days: Array<{ day: number; date: Date }> = [];
  for (let i = 0; i < startOffset; i++) {
    const prevDate = new Date(viewYear, viewMonth, -startOffset + i + 1);
    days.push({ day: prevDate.getDate(), date: prevDate });
  }
  for (let d = 1; d <= totalDays; d++) {
    days.push({ day: d, date: new Date(viewYear, viewMonth, d) });
  }

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-14 w-full items-center justify-between rounded-[1.125rem] border bg-surface pl-4 pr-3 text-[15px] shadow-[0_8px_24px_hsl(var(--color-text)/0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 disabled:cursor-not-allowed disabled:opacity-50',
          !selectedDate ? 'text-text-muted/80' : 'text-text',
          'border-border',
        )}
      >
        <span>{displayValue || placeholder}</span>
        <svg
          className="h-4 w-4 text-text-muted"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-border bg-surface p-3 shadow-lg">
          {/* Month / Year navigation */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                if (viewMonth === 0) {
                  setViewMonth(11);
                  setViewYear((y) => y - 1);
                } else {
                  setViewMonth((m) => m - 1);
                }
              }}
              className="rounded-md p-1 text-text-muted hover:bg-surface-elevated"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold capitalize">
              {monthNames[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={() => {
                if (viewMonth === 11) {
                  setViewMonth(0);
                  setViewYear((y) => y + 1);
                } else {
                  setViewMonth((m) => m + 1);
                }
              }}
              className="rounded-md p-1 text-text-muted hover:bg-surface-elevated"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day names */}
          <div className="mb-1 grid grid-cols-7 text-center text-xs font-semibold text-text-muted">
            {dayNames.map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 text-center text-sm">
            {days.map((d, i) => {
              const isCurrentMonth =
                d.date.getMonth() === viewMonth && d.date.getFullYear() === viewYear;
              const isSelected =
                selectedDate &&
                d.date.getFullYear() === selectedDate.getFullYear() &&
                d.date.getMonth() === selectedDate.getMonth() &&
                d.date.getDate() === selectedDate.getDate();
              const isToday =
                d.date.getFullYear() === new Date().getFullYear() &&
                d.date.getMonth() === new Date().getMonth() &&
                d.date.getDate() === new Date().getDate();

              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    const iso = d.date.toISOString().slice(0, 10);
                    onChange(iso);
                    setOpen(false);
                  }}
                  className={cn(
                    'rounded-md py-1 transition-colors',
                    isSelected
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : isCurrentMonth
                        ? 'text-text hover:bg-surface-elevated'
                        : 'text-text-muted/40',
                    isToday && !isSelected && 'ring-1 ring-border',
                  )}
                >
                  {d.day}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <button
            type="button"
            onClick={() => {
              const today = new Date();
              const iso = today.toISOString().slice(0, 10);
              onChange(iso);
              setOpen(false);
            }}
            className="mt-2 w-full rounded-md px-2 py-1 text-xs text-primary hover:bg-primary/10"
          >
            {locale.startsWith('fr') ? "Aujourd'hui" : 'Today'}
          </button>
        </div>
      )}
    </div>
  );
}
